import axios from 'axios';
import {
  type PlanSku,
  type PricingConfig,
} from 'hooks/billing/usePricingConfig';
import { StatusCodes } from 'http-status-codes';
import {
  SUBSCRIPTION_STATE,
  type SubscriptionState,
} from 'network/httpClients/BillingClient/api.schema';
import { type AccountInfoResponse } from 'types/billing';

/**
 * Monthly reviews a credit pool covers, at the configured credits-per-review rate.
 * Mirrors the GTM table (e.g. 5,000 credits ÷ 140 ≈ 36 reviews).
 *
 * @param credits - Total credits in the plan.
 * @param cfg - Pricing config supplying `creditsPerReview`.
 * @returns Rounded monthly review count the credit pool supports.
 */
export const estimateReviewsCount = (
  credits: number,
  cfg: PricingConfig,
): number => Math.round(credits / cfg.creditsPerReview);

// Matches the GTM sizing table assumption. If BE changes this rate, update here too.
const PRS_PER_ENGINEER_PER_MONTH = 4;

/**
 * Approximate number of engineers a credit pool is designed for,
 * based on credits-per-review, reviews-per-PR, and PRs per engineer per month.
 *
 * @param credits - Total credits in the plan.
 * @param cfg - Pricing config supplying `creditsPerReview` and `reviewsPerPr`.
 * @returns Rounded engineer headcount the credit pool is sized for.
 */
export const estimateEngineersCount = (
  credits: number,
  cfg: PricingConfig,
): number =>
  Math.round(
    credits /
      (cfg.creditsPerReview * cfg.reviewsPerPr * PRS_PER_ENGINEER_PER_MONTH),
  );

type AccountTrial = NonNullable<AccountInfoResponse['trial']>;

/** Badge variants used by the trial badge (subset of the DS Badge variants). */
type TrialBadgeVariant = 'neutral' | 'danger';

export interface TrialBadgeViewModel {
  /** i18n key suffix under `billing.popup`. */
  translationKey: string;
  variant: TrialBadgeVariant;
  /** i18next interpolation values, present only for the active-trial copy. */
  interpolation?: Record<string, string>;
}

/**
 * Maps the account trial object to the popup header badge view-model.
 * - Active trial → neutral "Day X of Y · Trial".
 * - Lapsed trial (object present, `active: false`) → danger "Trial ended",
 *   matching the red trial-end treatment on the status pill and hero.
 * - No trial (null/undefined) → undefined, i.e. render nothing.
 *
 * Paid accounts are excluded upstream (the caller passes undefined for paid),
 * so a present-but-inactive trial here always means the trial has lapsed.
 *
 * @param trial - The trial object from /account-info, scoped to non-paid accounts.
 * @returns The badge view-model, or undefined when no badge should render.
 */
export const getTrialBadgeViewModel = (
  trial: AccountTrial | null | undefined,
): TrialBadgeViewModel | undefined => {
  if (!trial) {
    return undefined;
  }

  if (trial.active) {
    return {
      translationKey: 'trial-badge',
      variant: 'neutral',
      interpolation: {
        day: String(trial.dayNumber),
        total: String(trial.totalDays),
      },
    };
  }

  return {
    translationKey: 'trial-badge-ended',
    variant: 'danger',
  };
};

/**
 * Maps a failed create-checkout-session request to the popup error's i18n key
 * suffix under `billing.popup`.
 *
 * A 403 is the BE owner-gate (`verify_billing_account_owner`, PLT-4289): the
 * caller isn't the account's recorded payer / billing owner, so a non-payer
 * member of a paid self-serve account can't start checkout. That gets the
 * informative "no permission" copy; every other failure is treated as transient
 * and keeps the generic "try again" copy.
 *
 * @param error - The error thrown by the create-checkout-session mutation.
 * @returns The i18n key suffix to render under the checkout CTA.
 */
export const getCheckoutErrorKey = (error: unknown): string => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;

  return status === StatusCodes.FORBIDDEN
    ? 'checkout-unauthorized'
    : 'checkout-error';
};

/**
 * Resolves the current plan's monthly credit count for the downgrade comparison,
 * independent of carousel-ladder membership. Prefers the matched ladder rung, but
 * falls back to parsing planSku — which is the credit count as a string per
 * usePricingConfig — so a plan sitting outside the carousel still classifies a
 * smaller-pack selection as a downgrade rather than wrongly routing to checkout.
 *
 * @param currentPlan - The current ladder rung, when planSku maps to one.
 * @param planSku - The account's plan SKU (credit count as a string), if any.
 * @returns The current credit count, or undefined when neither source yields a finite number.
 */
export const resolveCurrentPlanCredits = (
  currentPlan: PlanSku | undefined,
  planSku: string | null | undefined,
): number | undefined => {
  if (currentPlan) {
    return currentPlan.credits;
  }

  const parsed = Number(planSku);

  return planSku && Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Whether the account owns a live subscription that must NOT be routed to a fresh
 * Stripe checkout (doing so would re-purchase a plan they already own). True only
 * for ACTIVE, PAST_DUE (dunning grace: payment failed but the plan is still live),
 * SCHEDULED_TO_CANCEL, and SCHEDULED_TO_CHANGE.
 *
 * Returns false for CANCELED as well as 'none'/undefined: CANCELED is terminal
 * (Stripe retries exhausted, the plan is gone), so the account has no live plan
 * and is eligible for a fresh purchase, same as 'none'.
 *
 * @param subscriptionState - The subscription state from GET /billing-info, if resolved.
 * @returns True when the account has a live subscription; false for canceled/none/undefined.
 */
export const getHasExistingSubscription = (
  subscriptionState: SubscriptionState | undefined,
): boolean =>
  subscriptionState === SUBSCRIPTION_STATE.ACTIVE ||
  subscriptionState === SUBSCRIPTION_STATE.PAST_DUE ||
  subscriptionState === SUBSCRIPTION_STATE.SCHEDULED_TO_CANCEL ||
  subscriptionState === SUBSCRIPTION_STATE.SCHEDULED_TO_CHANGE;
